import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Modal,
} from 'react-bootstrap';
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaCertificate,
  FaUser,
  FaBook,
  FaCalendar,
  FaLayerGroup,
  FaEye,
  FaFilePdf,
  FaImage,
  FaExternalLinkAlt,
  FaDownload,
} from 'react-icons/fa';
import PublicNavbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import PublicService from '../../services/publicService';
import '../../styles/verifyCertificate.css';

const VerifyCertificate = () => {
  const [certificateNumber, setCertificateNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificateImageUrl, setCertificateImageUrl] = useState(null);
  const [certificateFileName, setCertificateFileName] = useState('');
  const [imageError, setImageError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!certificateNumber.trim()) {
      setError('Please enter a certificate number');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setImageError(false);

    try {
      const response = await PublicService.verifyCertificate(certificateNumber);
      console.log("=== API Response ===");
      console.log("Full response:", response);
      console.log("Certificate path:", response.data?.certificate?.certificate_path);
      console.log("Certificate name:", response.data?.certificate?.certificate_name);
      setResult(response.data);
    } catch (err) {
      setError('Failed to verify certificate. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Check if certificate has an image file
  const hasCertificateImage = () => {
    const path = result?.certificate?.certificate_path;
    return path && path !== null && path !== '' && path !== 'null' && path !== 'undefined';
  };

  // Get file extension for icon
  const getFileIcon = () => {
    if (!result?.certificate?.certificate_name) return <FaCertificate />;
    const fileName = result.certificate.certificate_name.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={24} />;
    } else if (fileName.match(/\.(jpg|jpeg|png|gif)$/)) {
      return <FaImage className="text-primary" size={24} />;
    }
    return <FaCertificate className="text-warning" size={24} />;
  };

  // Handle view certificate - Dynamic URL for both localhost and live server
  const handleViewCertificate = () => {
    if (!hasCertificateImage()) return;
    
    setImageError(false);
    
    // Get base URL dynamically
    const baseURL = `${window.location.protocol}/backend/${window.location.host}/`;
    
    // Clean the certificate path (remove any leading slashes)
    let cleanPath = result.certificate.certificate_path;
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    if (cleanPath.startsWith('./')) {
      cleanPath = cleanPath.substring(2);
    }
    
    // Construct the full URL
    const imageUrl = `${baseURL}/${cleanPath}`;
    
    console.log("=== Image URL Debug ===");
    console.log("Original path:", result.certificate.certificate_path);
    console.log("Cleaned path:", cleanPath);
    console.log("Base URL:", baseURL);
    console.log("Full Image URL:", imageUrl);
    
    setCertificateImageUrl(imageUrl);
    setCertificateFileName(result.certificate.certificate_name || 'certificate');
    setShowCertificateModal(true);
  };

  // Handle image load error
  const handleImageError = () => {
    console.error("Failed to load image:", certificateImageUrl);
    setImageError(true);
  };

  // Handle download certificate
  const handleDownloadCertificate = () => {
    if (!certificateImageUrl) return;
    
    const link = document.createElement('a');
    link.href = certificateImageUrl;
    link.download = certificateFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="verify-page">
      <PublicNavbar />
      
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            {/* Header */}
            <div className="text-center mb-5">
              <FaCertificate size={60} className="text-primary mb-3" />
              <h1 className="display-5 fw-bold">Certificate Verification</h1>
              <p className="text-muted">
                Enter the certificate number to verify its authenticity
              </p>
            </div>

            {/* Verification Form */}
            <Card className="shadow-lg border-0 mb-4">
              <Card.Body className="p-4">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Certificate Number</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="e.g., CERT-2026-0001"
                        value={certificateNumber}
                        onChange={(e) => setCertificateNumber(e.target.value)}
                        disabled={loading}
                        size="lg"
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <FaSearch />
                        )}
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      Enter the certificate number exactly as shown on the certificate
                    </Form.Text>
                  </Form.Group>
                </Form>

                {/* Error Message */}
                {error && (
                  <Alert variant="danger" className="mt-3">
                    <FaTimesCircle className="me-2" />
                    {error}
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Verification Result */}
            {result && (
              <Card className={`shadow-lg border-0 result-card ${result.valid ? 'valid' : 'invalid'}`}>
                <Card.Body className="p-4">
                  {result.valid ? (
                    // Valid Certificate
                    <>
                      <div className="text-center mb-4">
                        <div className="success-icon mb-3">
                          <FaCheckCircle size={60} className="text-success" />
                        </div>
                        <h3 className="text-success fw-bold">✓ VALID CERTIFICATE</h3>
                      </div>

                      <div className="certificate-details">
                        <h5 className="mb-3">Certificate Details</h5>
                        
                        <div className="detail-item mb-3">
                          <div className="d-flex align-items-center">
                            <FaCertificate className="text-primary me-2" />
                            <strong>Certificate Number:</strong>
                          </div>
                          <p className="ms-4 mb-0">{result.certificate.number}</p>
                        </div>

                        <div className="detail-item mb-3">
                          <div className="d-flex align-items-center">
                            <FaUser className="text-success me-2" />
                            <strong>Student Name:</strong>
                          </div>
                          <p className="ms-4 mb-0">{result.certificate.student_name}</p>
                          <small className="ms-4 text-muted">{result.certificate.student_email}</small>
                        </div>

                        <div className="detail-item mb-3">
                          <div className="d-flex align-items-center">
                            <FaBook className="text-info me-2" />
                            <strong>Course:</strong>
                          </div>
                          <p className="ms-4 mb-0">{result.certificate.course_name}</p>
                          <small className="ms-4 text-muted">Duration: {result.certificate.duration}</small>
                        </div>

                        <div className="detail-item mb-3">
                          <div className="d-flex align-items-center">
                            <FaLayerGroup className="text-warning me-2" />
                            <strong>Batch:</strong>
                          </div>
                          <p className="ms-4 mb-0">{result.certificate.batch_name}</p>
                        </div>

                        <div className="detail-item mb-3">
                          <div className="d-flex align-items-center">
                            <FaCalendar className="text-danger me-2" />
                            <strong>Issue Date:</strong>
                          </div>
                          <p className="ms-4 mb-0">{formatDate(result.certificate.issue_date)}</p>
                        </div>
                      </div>

                      {/* View Certificate Button - Only show if image exists */}
                      {hasCertificateImage() && (
                        <div className="text-center mt-4">
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={handleViewCertificate}
                            className="px-4"
                          >
                            <FaEye className="me-2" />
                            View Certificate
                          </Button>
                          <p className="text-muted small mt-2">
                            <FaExternalLinkAlt className="me-1" size={12} />
                            Click to view the original certificate document
                          </p>
                        </div>
                      )}

                      {/* No Image Notice */}
                      {!hasCertificateImage() && (
                        <Alert variant="info" className="mt-4">
                          <FaCertificate className="me-2" />
                          This certificate has been verified but the image file is not available.
                        </Alert>
                      )}

                      <Alert variant="success" className="mt-4">
                        <FaCheckCircle className="me-2" />
                        This certificate is valid and issued by Sensible Academy.
                      </Alert>
                    </>
                  ) : (
                    // Invalid Certificate
                    <>
                      <div className="text-center mb-4">
                        <div className="error-icon mb-3">
                          <FaTimesCircle size={60} className="text-danger" />
                        </div>
                        <h3 className="text-danger fw-bold">✗ INVALID CERTIFICATE</h3>
                      </div>

                      <Alert variant="danger">
                        <p className="mb-2">
                          <strong>{result.message}</strong>
                        </p>
                        <p className="mb-0 small">
                          Please check the certificate number and try again. 
                          If you believe this is an error, contact the institute.
                        </p>
                      </Alert>

                      <div className="text-center mt-4">
                        <h6>Contact Institute:</h6>
                        <p className="mb-1">📞 77228-99444, 93013-51989</p>
                        <p className="mb-0">📧 piyush@sensible-computers.com</p>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      {/* Certificate View Modal */}
      <Modal
        show={showCertificateModal}
        onHide={() => {
          setShowCertificateModal(false);
          setImageError(false);
        }}
        size="lg"
        centered
        className="certificate-view-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {getFileIcon()}
            <span className="ms-2">Certificate Document</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {certificateImageUrl && !imageError && (
            <div className="certificate-viewer">
              {certificateFileName?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={`${certificateImageUrl}#toolbar=0&navpanes=0`}
                  title="Certificate PDF"
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                  }}
                  onError={handleImageError}
                />
              ) : (
                <img
                  src={certificateImageUrl}
                  alt="Certificate"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                  }}
                  onError={handleImageError}
                />
              )}
            </div>
          )}
          {imageError && (
            <div className="text-center p-5">
              <FaImage size={48} className="text-muted mb-3" />
              <p className="text-danger">Failed to load certificate image.</p>
              <p className="text-muted small">
                Certificate Number: {result?.certificate?.number}<br />
                Student: {result?.certificate?.student_name}
              </p>
              <Button variant="outline-primary" onClick={handleDownloadCertificate}>
                <FaDownload className="me-2" /> Try Download
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCertificateModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={handleDownloadCertificate}>
            <FaDownload className="me-2" /> Download
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </div>
  );
};

export default VerifyCertificate;